// engineNarration — JS-side wrapper that routes narration playback
// through BoodaVoice.playNarration on iOS native. The native engine
// hosts the playback on the SAME AVAudioEngine as the speech
// recognizer, so voice-processing IO can AEC narration audio out of
// the mic input — which is what unlocks "Booda listens through
// chadwick narration."
//
// Returns a promise that resolves when narration ends (natural end OR
// interruption by another playEngineNarration call) and rejects if
// the engine isn't available / decode fails. Callers fall back to
// their existing Web Audio / HTML5 path on rejection.
//
// Single-clip semantics: only one engine narration plays at a time.
// A new playEngineNarration call resolves the previous clip's
// promise (so callers can release floor claims etc.) before starting
// the new clip.

import BoodaVoice from "./boodaVoice.js";

let active = null;             // { url, resolve } | null
let endListenerAttached = false;
let wakePauseListenerAttached = false;
let wakePauseResumeTimer = null;

async function ensureEndListener() {
  if (endListenerAttached || !BoodaVoice.supported) return;
  endListenerAttached = true;
  try {
    await BoodaVoice.addListener("narrationEnded", () => {
      const a = active;
      active = null;
      if (a) a.resolve();
    });
  } catch (e) {
    console.warn("[engineNarration] addListener failed:", e);
    endListenerAttached = false;
  }
}

// Centralized wake-pause: pauses WHOEVER is currently playing through
// the engine narrationPlayer when "Hey Booda" fires, regardless of
// caller (Chadwick step line, voiceline, per-step recipe narration).
// Single source of truth for the policy — moves the per-component
// listener that used to live in StepNarrationAudio up to the engine
// boundary so non-narrated recipes (Chadwick step lines) get the
// same pause-on-wake behavior as narrated ones. Auto-resumes after
// the wake window; commands that change step naturally supersede
// via a fresh playEngineNarration call, which the resume's
// no-active-clip guard handles cleanly.
function ensureWakePauseListener() {
  if (wakePauseListenerAttached) return;
  if (typeof window === "undefined") return;
  if (!BoodaVoice.supported) return;
  wakePauseListenerAttached = true;
  console.log("[engineNarration] wake-pause listener attached");
  window.addEventListener("voice:wake-paused", (e) => {
    const resumeAfter = Number(e?.detail?.resumeAfterMs) || 4000;
    // Always call pauseNarration unconditionally. The Swift side has
    // its own guard (returns early if nothing is playing), so a
    // misfire is harmless. The prior `if (!active) return` JS-side
    // bail was racy — `active` can be null in legitimate scenarios
    // where Swift is still mid-play (e.g. between scheduleBuffer
    // and the buffer-end completion handler firing).
    console.log(`[engineNarration] wake heard — calling pauseNarration (active=${!!active})`);
    BoodaVoice.pauseNarration().catch((err) => {
      console.warn("[engineNarration] pauseNarration call failed:", err?.message || err);
    });
    if (wakePauseResumeTimer) clearTimeout(wakePauseResumeTimer);
    wakePauseResumeTimer = setTimeout(() => {
      wakePauseResumeTimer = null;
      console.log(`[engineNarration] wake window expired — calling resumeNarration (active=${!!active})`);
      BoodaVoice.resumeNarration().catch(() => {});
    }, resumeAfter);
  });
}

function resolveUrl(url) {
  if (!url) return null;
  if (typeof url !== "string") return null;
  // Already absolute (https://supabase.../mp3, file://, etc.) — pass through.
  if (/^[a-z]+:\/\//i.test(url)) return url;
  // Relative — resolve against the current page origin so Swift gets
  // a fully-formed URL it can handle (capacitor://localhost/... on
  // iOS Capacitor, which BoodaVoice resolves to Bundle.main).
  if (typeof window !== "undefined") {
    try { return new URL(url, window.location.origin).toString(); }
    catch { return url; }
  }
  return url;
}

// Detect the "engine not running" error that BoodaVoiceEngine throws
// when playNarration races with engine.start() (banner-resume mounts
// CookMode + StepNarrationAudio before BoodaVoice.start has actually
// brought the AVAudioEngine up). The Swift side rejects with the
// localizedDescription, so we substring-match on it.
function isEngineNotRunningError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return msg.includes("engine not running") || msg.includes("audio engine not running");
}

export function playEngineNarration(url) {
  if (!BoodaVoice.supported) {
    return Promise.reject(new Error("BoodaVoice not supported on this platform"));
  }
  const fullUrl = resolveUrl(url);
  if (!fullUrl) return Promise.reject(new Error("invalid narration url"));
  ensureEndListener();
  ensureWakePauseListener();

  // Pre-resolve any prior clip — Swift will stop the prior on the
  // native side, but JS callers (chadwickAudio, playVoiceline) need
  // their floor-claim release to happen on the same tick we hand off.
  if (active) {
    const prior = active;
    active = null;
    prior.resolve();
  }

  // Retry on ANY transient rejection. The previous version tried to
  // be precise about WHICH errors warrant retry (engine not running),
  // but the Capacitor bridge can wrap errors in shapes the regex
  // doesn't catch — and any error during the first ~1.5s of CookMode
  // mount is almost always engine-still-spinning-up. Retrying
  // unconditionally up to MAX_ATTEMPTS with a short interval is
  // simpler and more robust. A truly missing file gets retried 4×
  // before giving up; that's annoying but not catastrophic.
  const MAX_ATTEMPTS = 6;
  const RETRY_MS = 350;

  return new Promise((resolve, reject) => {
    active = { url: fullUrl, resolve };
    const tryPlay = (attempt) => {
      BoodaVoice.playNarration(fullUrl).catch((e) => {
        // Superseded — bail without recovering.
        if (!active || active.url !== fullUrl) return;
        const msg = e?.message || e?.errorMessage || String(e);
        console.log(`[engineNarration] play attempt ${attempt}/${MAX_ATTEMPTS} failed: ${msg}`);
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (!active || active.url !== fullUrl) return;
            tryPlay(attempt + 1);
          }, RETRY_MS);
          return;
        }
        if (active && active.url === fullUrl) active = null;
        reject(e);
      });
    };
    tryPlay(1);
  });
}

export function stopEngineNarration() {
  if (!BoodaVoice.supported) return Promise.resolve();
  if (active) {
    const a = active;
    active = null;
    a.resolve();
  }
  return BoodaVoice.stopNarration().catch(() => {});
}

// Pause / resume — playhead held in place between calls. Used to mute
// narration while the wake-word listening window is open, so the user
// can hear themselves and the action chime plays cleanly. The active
// promise is NOT resolved — callers shouldn't release floor claims on
// pause; the clip is logically still playing.
export function pauseEngineNarration() {
  if (!BoodaVoice.supported) return Promise.resolve();
  return BoodaVoice.pauseNarration().catch(() => {});
}

export function resumeEngineNarration() {
  if (!BoodaVoice.supported) return Promise.resolve();
  return BoodaVoice.resumeNarration().catch(() => {});
}

export const engineNarrationSupported = () => BoodaVoice.supported;
